/**
 * Activity Heatmap Utility Functions
 * Color calculation and date helpers for the GitHub-style contribution heatmap
 */

// Blue-Purple gradient color scheme
export const HEATMAP_COLORS = {
  empty: { light: '#e5e7eb', dark: '#374151' },
  levels: [
    { light: '#dbeafe', dark: '#1e3a5f' },  // Level 1 - Light blue
    { light: '#93c5fd', dark: '#3b82f6' },  // Level 2 - Blue
    { light: '#818cf8', dark: '#6366f1' },  // Level 3 - Indigo
    { light: '#a78bfa', dark: '#8b5cf6' }   // Level 4 - Purple
  ]
}

// Grid layout constants
export const CELL_SIZE = 10
export const CELL_GAP = 2
export const WEEKS = 52
export const DAYS_PER_WEEK = 7

/**
 * Calculate intensity level (0-4) based on activity count
 * @param {number} count - Activity count for the day
 * @param {number} maxCount - Maximum activity count in the dataset
 * @returns {number} Intensity level 0-4
 */
export function calculateIntensity(count, maxCount) {
  if (count === 0 || !count) return 0
  if (maxCount === 0) return 1

  const ratio = count / maxCount
  if (ratio <= 0.25) return 1  // Low
  if (ratio <= 0.50) return 2  // Medium
  if (ratio <= 0.75) return 3  // High
  return 4                      // Max
}

/**
 * Get color for a given intensity level
 * @param {number} intensity - Intensity level 0-4
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {string} CSS color value
 */
export function getColor(intensity, isDark = false) {
  const mode = isDark ? 'dark' : 'light'

  if (intensity === 0) {
    return HEATMAP_COLORS.empty[mode]
  }

  return HEATMAP_COLORS.levels[intensity - 1][mode]
}

/**
 * Format date for display in tooltip
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string (e.g., "Monday, Jan 15, 2025")
 */
export function formatDateForTooltip(date) {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

/**
 * Get ISO date string (YYYY-MM-DD) from Date object
 * @param {Date} date - Date object
 * @returns {string} ISO date string
 */
export function getDateKey(date) {
  return date.toISOString().split('T')[0]
}

/**
 * Generate array of dates for the heatmap grid (52 weeks)
 * Starts from 364 days ago and ends today
 * Organized by week columns
 * @returns {Array<Array<{date: Date, dateKey: string}>>} Array of weeks, each containing 7 days
 */
export function generateDateGrid() {
  const weeks = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Start from 364 days ago (52 weeks * 7 days)
  const startDate = new Date(today)
  startDate.setDate(startDate.getDate() - 364)

  // Adjust to start from Sunday
  const dayOfWeek = startDate.getDay()
  startDate.setDate(startDate.getDate() - dayOfWeek)

  // Generate 52 weeks + partial current week
  let currentDate = new Date(startDate)

  while (currentDate <= today || weeks.length < WEEKS) {
    const week = []

    for (let day = 0; day < DAYS_PER_WEEK; day++) {
      const date = new Date(currentDate)
      week.push({
        date,
        dateKey: getDateKey(date),
        isFuture: date > today
      })
      currentDate.setDate(currentDate.getDate() + 1)
    }

    weeks.push(week)

    if (weeks.length >= WEEKS + 1) break
  }

  return weeks.slice(0, WEEKS)
}

/**
 * Get month labels for the heatmap
 * @param {Array<Array<{date: Date}>>} weeks - Week grid data
 * @returns {Array<{label: string, weekIndex: number}>} Month labels with positions
 */
export function getMonthLabels(weeks) {
  const labels = []
  let lastMonth = -1

  weeks.forEach((week, weekIndex) => {
    // Check the first day of the week
    const firstDay = week[0]
    if (!firstDay) return

    const month = firstDay.date.getMonth()

    if (month !== lastMonth) {
      labels.push({
        label: firstDay.date.toLocaleDateString('en-US', { month: 'short' }),
        weekIndex
      })
      lastMonth = month
    }
  })

  return labels
}

/**
 * Get day labels for the Y-axis
 * @returns {Array<{label: string, dayIndex: number}>} Day labels (Mon, Wed, Fri)
 */
export function getDayLabels() {
  return [
    { label: 'Mon', dayIndex: 1 },
    { label: 'Wed', dayIndex: 3 },
    { label: 'Fri', dayIndex: 5 }
  ]
}
